(() => {
  const widgetSelector = '.jdgm-review-widget .jdgm-rev-widg';
  const widgetStates = new WeakMap();
  const expansionStates = new WeakMap();
  const pendingWidgets = new Set();
  const reviewLoaders = new Set();
  let nativeReviewEventsReady = false;
  let nextTrackId = 1;
  let nextBodyId = 1;
  let refreshFrame = 0;

  function placeReviewer(review) {
    const content = review.querySelector('.jdgm-rev__content');
    const profile = review.querySelector('.jdgm-row-profile');
    if (content && profile && content.lastElementChild !== profile) content.append(profile);
  }

  function updateReviewExpansion(review) {
    const body = review.querySelector('.jdgm-rev__body');
    let state = expansionStates.get(review);
    const canCollapse = body && body.textContent.replace(/\s+/g, ' ').trim().length > 220
      && !body.querySelector('a, button, [role="button"]');

    if (state && (state.body !== body || !canCollapse)) {
      state.body.classList.remove('emery-review-collapsible');
      state.button.remove();
      review.classList.remove('emery-review-expanded');
      expansionStates.delete(review);
      state = null;
    }
    if (!canCollapse) return;

    if (!state) {
      let bodyId = body.getAttribute('id');
      if (!bodyId) {
        do {
          bodyId = `emery-review-body-${nextBodyId++}`;
        } while (document.getElementById(bodyId));
        body.setAttribute('id', bodyId);
      }
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'emery-review-more';
      button.setAttribute('aria-controls', bodyId);
      button.setAttribute('aria-expanded', 'false');
      button.textContent = 'Read more';
      button.addEventListener('click', () => {
        const expanded = review.classList.toggle('emery-review-expanded');
        button.setAttribute('aria-expanded', String(expanded));
        button.textContent = expanded ? 'Read less' : 'Read more';
      });
      state = { body, button };
      expansionStates.set(review, state);
    }
    body.classList.add('emery-review-collapsible');
    if (body.nextElementSibling !== state.button) body.after(state.button);
  }

  function updateSummary(widget) {
    const summary = widget.querySelector('.jdgm-rev-widg__summary');
    if (!summary) return;

    const actions = widget.querySelector('.jdgm-widget-actions-wrapper');
    if (actions && actions.parentElement !== summary) summary.append(actions);

    const inner = summary.querySelector('.jdgm-rev-widg__summary-inner') || summary;
    const stars = inner.querySelector('.jdgm-rev-widg__summary-stars');
    const ratingSource = widget.hasAttribute('data-average-rating')
      ? widget
      : widget.closest('.jdgm-review-widget[data-average-rating]') || widget.querySelector('[data-average-rating]');
    const rating = Number.parseFloat(ratingSource?.getAttribute('data-average-rating'));
    if (!stars || !Number.isFinite(rating)) return;

    let score = summary.querySelector('.emery-review-score');
    if (!score) {
      score = document.createElement('div');
      score.className = 'emery-review-score';
    }
    const text = `${rating.toFixed(1)} Rating`;
    if (score.textContent !== text) score.textContent = text;
    if (stars.nextElementSibling !== score) stars.after(score);
  }

  function placeDetails(widget) {
    let details = widget.querySelector(':scope > details.emery-review-details');
    const currentNode = (selector) => {
      const nodes = Array.from(widget.querySelectorAll(selector));
      return nodes.find((node) => !details?.contains(node)) || nodes[0];
    };
    const histogram = currentNode('.jdgm-histogram');
    const media = currentNode('.jdgm-row-media');
    if (!histogram && !media) return;

    if (!details) {
      details = document.createElement('details');
      details.className = 'emery-review-details';
      const summary = document.createElement('summary');
      summary.textContent = 'Rating breakdown & customer photos';
      details.append(summary);
      widget.append(details);
    }
    // A header refresh can insert new native nodes while the old ones remain here.
    for (const [selector, current] of [['.jdgm-histogram', histogram], ['.jdgm-row-media', media]]) {
      details.querySelectorAll(`:scope > ${selector}`).forEach((node) => {
        if (node !== current) node.remove();
      });
    }
    const summary = details.querySelector(':scope > summary');
    if (histogram && summary.nextElementSibling !== histogram) summary.after(histogram);
    const preceding = histogram || summary;
    if (media && preceding.nextElementSibling !== media) preceding.after(media);
  }

  function createButton(direction, trackId) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = `emery-review-${direction}`;
    button.setAttribute('aria-label', direction === 'prev' ? 'Previous reviews' : 'Next reviews');
    button.setAttribute('aria-controls', trackId);
    const icon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    icon.setAttribute('viewBox', '0 0 24 24');
    icon.setAttribute('width', '20');
    icon.setAttribute('height', '20');
    icon.setAttribute('fill', 'none');
    icon.setAttribute('aria-hidden', 'true');
    icon.setAttribute('focusable', 'false');
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', direction === 'prev' ? 'm14 6-6 6 6 6' : 'm10 6 6 6-6 6');
    path.setAttribute('stroke', 'currentColor');
    path.setAttribute('stroke-width', '1.8');
    path.setAttribute('stroke-linecap', 'round');
    path.setAttribute('stroke-linejoin', 'round');
    icon.append(path);
    button.append(icon);
    return button;
  }

  function getCards(track) {
    return Array.from(track.children).filter((node) => node.matches('.jdgm-rev') && node.getClientRects().length);
  }

  function setupTrack(track) {
    let trackId = track.getAttribute('id');
    if (!trackId) {
      do {
        trackId = `emery-review-track-${nextTrackId++}`;
      } while (document.getElementById(trackId));
      track.setAttribute('id', trackId);
    }
    track.setAttribute('role', 'region');
    track.setAttribute('tabindex', '0');
    track.setAttribute('aria-label', 'Customer reviews');

    const navigation = document.createElement('div');
    navigation.className = 'emery-review-navigation';
    navigation.setAttribute('role', 'group');
    navigation.setAttribute('aria-label', 'Review navigation');
    const previous = createButton('prev', trackId);
    const next = createButton('next', trackId);
    const status = document.createElement('span');
    status.className = 'sr-only';
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    status.setAttribute('aria-atomic', 'true');
    navigation.append(previous, next, status);

    const abortController = new AbortController();
    const observedCards = new Set();
    let previousCards = Array.from(track.children).filter((node) => node.matches('.jdgm-rev'));
    let updateFrame = 0;
    const resizeObserver = typeof ResizeObserver === 'function' ? new ResizeObserver(scheduleUpdate) : null;

    function update() {
      updateFrame = 0;
      if (!track.isConnected) return;
      const cards = getCards(track);
      navigation.hidden = cards.length === 0;

      if (resizeObserver) {
        for (const card of observedCards) {
          if (!cards.includes(card)) {
            resizeObserver.unobserve(card);
            observedCards.delete(card);
          }
        }
        for (const card of cards) {
          if (!observedCards.has(card)) {
            observedCards.add(card);
            resizeObserver.observe(card);
          }
        }
      }

      const bounds = track.getBoundingClientRect();
      const rtl = getComputedStyle(track).direction === 'rtl';
      const first = cards[0]?.getBoundingClientRect();
      const last = cards[cards.length - 1]?.getBoundingClientRect();
      const noOverflow = track.scrollWidth <= track.clientWidth + 2;
      previous.disabled = !first || noOverflow || (rtl ? first.right <= bounds.right + 2 : first.left >= bounds.left - 2);
      next.disabled = !last || noOverflow || (rtl ? last.left >= bounds.left - 2 : last.right <= bounds.right + 2);

      const visible = cards.flatMap((card, index) => {
        const rect = card.getBoundingClientRect();
        return rect.right > bounds.left + 1 && rect.left < bounds.right - 1 ? [index + 1] : [];
      });
      const message = visible.length
        ? `Showing reviews ${visible[0]} to ${visible[visible.length - 1]} of ${cards.length}`
        : 'No reviews to show';
      if (status.textContent !== message) status.textContent = message;
    }

    function scheduleUpdate() {
      if (!updateFrame) updateFrame = requestAnimationFrame(update);
    }

    function scroll(direction) {
      const first = getCards(track)[0];
      if (!first) return;
      const style = getComputedStyle(track);
      const distance = first.getBoundingClientRect().width + (Number.parseFloat(style.columnGap) || 0);
      const sign = style.direction === 'rtl' ? -1 : 1;
      track.scrollBy({
        left: distance * direction * sign,
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'
      });
      scheduleUpdate();
    }

    previous.addEventListener('click', () => scroll(-1), { signal: abortController.signal });
    next.addEventListener('click', () => scroll(1), { signal: abortController.signal });
    track.addEventListener('scroll', scheduleUpdate, { passive: true, signal: abortController.signal });
    if (resizeObserver) resizeObserver.observe(track);
    else window.addEventListener('resize', scheduleUpdate, { signal: abortController.signal });

    return {
      track,
      navigation,
      refresh() {
        if (track.nextElementSibling !== navigation) track.after(navigation);
        const cards = Array.from(track.children).filter((node) => node.matches('.jdgm-rev'));
        // Reset replaced/sorted pages, while preserving position when more cards append.
        if (previousCards.some((card, index) => cards[index] !== card)) {
          track.scrollTo({ left: 0, behavior: 'instant' });
        }
        previousCards = cards;
        scheduleUpdate();
      },
      dispose() {
        abortController.abort();
        resizeObserver?.disconnect();
        if (updateFrame) cancelAnimationFrame(updateFrame);
        navigation.remove();
      }
    };
  }

  function stopReviewLoad(loader) {
    loader.controller?.abort();
    loader.controller = null;
    loader.track?.removeAttribute('aria-busy');
  }

  function reviewRequestSignature(params) {
    return JSON.stringify(Object.keys(params).filter((key) => !['page', 'per_page', 'ts'].includes(key))
      .sort().map((key) => [key, params[key]]));
  }

  function installNativeReviewEvents() {
    if (nativeReviewEventsReady || !window.jdgm?.$) return;
    nativeReviewEventsReady = true;
    // Judge.me replaces the review list when its sort, search, or rating filter changes.
    window.jdgm.$(document).on('jdgm.beforeFetchingReviews.emeryAllReviews', () => {
      reviewLoaders.forEach((loader) => {
        const outer = loader.widget.closest('.jdgm-review-widget');
        if (!outer || !loader.signature) return;
        let signature;
        try {
          signature = reviewRequestSignature(window.jdgm.ajaxParamsFor(window.jdgm.$(outer)));
        } catch (_) {
          return;
        }
        if (signature === loader.signature) return;
        stopReviewLoad(loader);
        loader.status = 'waiting';
        loader.widget.classList.remove('emery-reviews-all-loaded');
      });
    });
    window.jdgm.$(document).on('jdgm.doneCustomizeReviews.emeryAllReviews', () => {
      reviewLoaders.forEach((loader) => queueWidget(loader.widget));
    });
  }

  function createReviewLoader(widget) {
    const loader = { widget, track: null, cards: [], signature: null, status: 'idle', controller: null };
    reviewLoaders.add(loader);
    return loader;
  }

  function disposeReviewLoader(loader) {
    if (!loader) return;
    stopReviewLoad(loader);
    reviewLoaders.delete(loader);
  }

  function refreshAllReviews(widget, loader, track) {
    installNativeReviewEvents();
    const cards = track ? Array.from(track.children).filter((node) => node.matches('.jdgm-rev')) : [];
    if (loader.track !== track || cards.length !== loader.cards.length
      || cards.some((card, index) => card !== loader.cards[index])) {
      stopReviewLoad(loader);
      loader.track = track;
      loader.cards = cards;
      loader.signature = null;
      loader.status = 'idle';
      widget.classList.remove('emery-reviews-all-loaded');
    }
    const native = window.jdgm;
    const outer = widget.closest('.jdgm-review-widget');
    if (!track || !outer || typeof native?.ajaxParamsFor !== 'function'
      || typeof native.customizeReviews !== 'function' || loader.status === 'waiting') return;

    let params;
    try {
      params = native.ajaxParamsFor(native.$(outer));
    } catch (_) {
      return;
    }
    if (!params.product_id) return;
    const signature = reviewRequestSignature(params);
    if (loader.signature !== signature) {
      stopReviewLoad(loader);
      loader.signature = signature;
      loader.status = 'idle';
      widget.classList.remove('emery-reviews-all-loaded');
    }
    if (loader.status !== 'idle') return;

    const pagination = widget.querySelector('.jdgm-rev-widg__body .jdgm-paginate');
    const sourceUrl = pagination?.getAttribute('data-url') || outer.getAttribute('data-url');
    let endpoint;
    try {
      endpoint = new URL(sourceUrl || 'https://api.judge.me/reviews/reviews_for_widget');
      if (endpoint.protocol !== 'https:' || !['api.judge.me', 'cdnwidget.judge.me'].includes(endpoint.hostname)
        || endpoint.pathname !== '/reviews/reviews_for_widget') return;
    } catch (_) {
      return;
    }
    loader.status = 'loading';
    const controller = new AbortController();
    loader.controller = controller;
    track.setAttribute('aria-busy', 'true');

    const isCurrent = () => widget.isConnected && track.isConnected && loader.track === track
      && loader.controller === controller && loader.signature === signature && !controller.signal.aborted;

    const timeout = setTimeout(() => {
      if (!isCurrent()) return;
      stopReviewLoad(loader);
      loader.status = 'failed';
    }, 20000);

    (async () => {
      const collected = new Map();
      let page = 1;
      let total = null;
      while (isCurrent()) {
        const url = new URL(endpoint);
        Object.entries({ ...params, per_page: 30, page }).forEach(([key, value]) => {
          if (value === undefined || value === null || value === '') return;
          if (Array.isArray(value)) value.forEach((item) => url.searchParams.append(`${key}[]`, item));
          else url.searchParams.set(key, value);
        });
        const response = await fetch(url, { signal: controller.signal, credentials: 'omit' });
        if (!response.ok) throw new Error('Review request failed');
        const result = await response.json();
        if (!isCurrent()) return;
        const count = Number(result.total_count);
        if (typeof result.html !== 'string' || !Number.isSafeInteger(count) || count < 0
          || Number(result.page) !== page || (total !== null && count !== total)) {
          throw new Error('Review response changed');
        }
        total = count;
        const parsed = new DOMParser().parseFromString(result.html, 'text/html');
        const received = parsed.querySelectorAll('.jdgm-rev-widg__reviews > .jdgm-rev');
        received.forEach((review) => {
          const id = review.getAttribute('data-review-id');
          if (!id) throw new Error('Review response is missing an ID');
          if (!collected.has(id)) collected.set(id, review);
        });
        // Judge.me's total can include reviews omitted from its public HTML. Its
        // paginator, rather than the rendered-card count, determines the last page.
        const pager = parsed.querySelector('.jdgm-paginate');
        const perPage = Number(pager?.getAttribute('data-per-page'));
        const currentPage = pager?.querySelector('.jdgm-curt')?.getAttribute('data-page');
        if (!pager || !Number.isSafeInteger(perPage) || perPage < 1 || perPage > 30
          || (currentPage && Number(currentPage) !== page)) {
          throw new Error('Review pagination was invalid');
        }
        const futurePages = Array.from(pager.querySelectorAll('[data-page]'))
          .map((link) => Number(link.getAttribute('data-page')))
          .filter((number) => Number.isSafeInteger(number) && number > page);
        if (!futurePages.length) break;
        const nextPage = Math.min(...futurePages);
        if (nextPage !== page + 1 || nextPage > Math.max(1, Math.ceil(total / perPage))) {
          throw new Error('Review pagination was invalid');
        }
        page = nextPage;
      }
      if (!isCurrent()) return;
      // A filter can change before its replacement list arrives. Never commit stale reviews.
      if (reviewRequestSignature(native.ajaxParamsFor(native.$(outer))) !== signature) {
        stopReviewLoad(loader);
        loader.status = 'waiting';
        return;
      }
      const complete = Array.from(collected.values());
      const previous = loader.cards;
      previous.forEach((review) => review.remove());
      track.append(...complete);
      loader.cards = complete;
      try {
        native.customizeReviews();
        native.setupMediaGallery?.(native.$(outer));
      } catch (error) {
        complete.forEach((review) => review.remove());
        track.append(...previous);
        loader.cards = previous;
        throw error;
      }
      loader.status = 'loaded';
      loader.controller = null;
      track.removeAttribute('aria-busy');
      widget.classList.add('emery-reviews-all-loaded');
      queueWidget(widget);
    })().catch(() => {
      if (!isCurrent()) return;
      loader.status = 'failed';
      loader.controller = null;
      track.removeAttribute('aria-busy');
      widget.classList.remove('emery-reviews-all-loaded');
      // Keep the original native cards and pagination available when loading fails.
    }).finally(() => clearTimeout(timeout));
  }

  function enhanceWidget(widget) {
    if (!widget.isConnected) return;
    const reviews = widget.querySelectorAll('.jdgm-rev');
    const existingState = widgetStates.get(widget);
    // Leave native empty widgets intact; a later async batch can activate this layout.
    if (!reviews.length && !existingState) return;

    widget.classList.add('emery-review-cards');
    reviews.forEach((review) => {
      placeReviewer(review);
      updateReviewExpansion(review);
    });
    updateSummary(widget);
    placeDetails(widget);

    const track = widget.querySelector('.jdgm-rev-widg__reviews');
    const state = existingState || { trackState: null, reviewLoader: createReviewLoader(widget) };
    if (state.trackState?.track !== track) {
      state.trackState?.dispose();
      state.trackState = track ? setupTrack(track) : null;
    }
    widgetStates.set(widget, state);
    state.trackState?.refresh();
    refreshAllReviews(widget, state.reviewLoader, track);
  }

  function queueWidget(widget) {
    if (!widget || !widget.isConnected) return;
    pendingWidgets.add(widget);
    if (!refreshFrame) {
      refreshFrame = requestAnimationFrame(() => {
        refreshFrame = 0;
        const widgets = Array.from(pendingWidgets);
        pendingWidgets.clear();
        widgets.forEach(enhanceWidget);
      });
    }
  }

  function collectWidgets(node, callback) {
    if (!(node instanceof Element)) return;
    if (node.matches(widgetSelector)) callback(node);
    node.querySelectorAll(widgetSelector).forEach(callback);
  }

  document.addEventListener('jdgm.finishLoadingCore', () => {
    document.querySelectorAll(widgetSelector).forEach(queueWidget);
  });
  document.querySelectorAll(widgetSelector).forEach(enhanceWidget);
  new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.target instanceof Element) {
        queueWidget(mutation.target.closest(widgetSelector));
        if (mutation.type === 'attributes') collectWidgets(mutation.target, queueWidget);
      }
      for (const node of mutation.addedNodes) collectWidgets(node, queueWidget);
      for (const node of mutation.removedNodes) {
        if (!(node instanceof Element)) continue;
        const removedWidgets = node.matches('.jdgm-rev-widg') ? [node] : [];
        removedWidgets.push(...node.querySelectorAll('.jdgm-rev-widg'));
        removedWidgets.forEach((widget) => {
          if (!widget.isConnected) {
            widgetStates.get(widget)?.trackState?.dispose();
            disposeReviewLoader(widgetStates.get(widget)?.reviewLoader);
            widgetStates.delete(widget);
            pendingWidgets.delete(widget);
          }
        });
      }
    }
  }).observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['data-average-rating', 'data-number-of-reviews']
  });
})();
